import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
/**
 * A pending in-app notification. `template` names the web app's body component and `content` is
 * a free-form object that template validates; neither is interpreted here or on the server.
 */
export const notificationSchema = z.object({
    id: z.string(),
    name: z.string(),
    /** A key into the web app's icon map, not a URL. */
    icon: z.string().optional(),
    template: z.string(),
    content: z.record(z.string(), z.unknown()),
    dismissible: z.boolean(),
    autoOpen: z.boolean(),
    expiresAt: z.string(),
    createdAt: z.string(),
});
export const notificationsResponseSchema = z.object({
    notifications: z.array(notificationSchema),
});
export const notificationsQueryKey = ['notifications'];
/**
 * How often an open tab checks for notifications created since it loaded. TanStack Query pauses
 * this while the tab is hidden and refetches when it becomes visible again.
 */
export const NOTIFICATIONS_POLL_MS = 2 * 60 * 1000;
/** The cached list without the given notification; an absent cache entry is left untouched. */
export const removeNotification = (response, id) => {
    if (!response?.data)
        return response;
    return {
        ...response,
        data: {
            ...response.data,
            notifications: response.data.notifications.filter((notification) => notification.id !== id),
        },
    };
};
/** The user's pending (unviewed, unexpired) notifications, newest first. */
export const useGetNotifications = (authenticatedRequest) => {
    return useQuery({
        queryKey: notificationsQueryKey,
        queryFn: () => authenticatedRequest({ url: '/notifications', method: 'GET' }, notificationsResponseSchema),
        refetchInterval: NOTIFICATIONS_POLL_MS,
    });
};
/**
 * Marks a notification viewed. It leaves the cached list at once, and comes back if the call
 * fails.
 */
export const useMarkNotificationViewed = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => 
        // 204 with no body, so there is no schema to validate against
        authenticatedRequest({ url: `/notifications/${id}/view`, method: 'POST' }, z.any()),
        onMutate: async (id) => {
            // Stop an in-flight refetch from landing on top of the optimistic value.
            await queryClient.cancelQueries({ queryKey: notificationsQueryKey });
            const previous = queryClient.getQueryData(notificationsQueryKey);
            queryClient.setQueryData(notificationsQueryKey, (current) => removeNotification(current, id));
            return { previous };
        },
        onError: (_error, _id, context) => {
            if (context?.previous) {
                queryClient.setQueryData(notificationsQueryKey, context.previous);
            }
        },
    });
};
